/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { instanceOf } from 'prop-types';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import styles from './master-teacher.module.css';
import { withCookies, cookieKeys, Cookies } from '../../common/auth/cookie_utils';
import {
  TLPBackend,
  calculateScores,
  calculateSiteScores,
  formatSchoolYear,
  getSchoolYearChoices,
} from '../../common/utils';

import {
  reportingSites,
  reportingGroups,
  initialReportingSite,
  reportingSiteMap,
} from '../../common/siteReporting';
import Plus from '../../assets/icons/plus.svg';
import StudentGroup from '../../components/StudentGroup/StudentGroup';
import StudentProfileBox from '../../components/StudentProfileBox/StudentProfileBox';
import StudentTable from '../../components/StudentTable/StudentTable';
import Graph from '../../components/Graph/Graph';
import DropdownMenu from '../../common/DropdownMenu/DropdownMenu';
import CreateStudentGroupModal from '../../components/CreateStudentGroupModal/CreateStudentGroupModal';
import CreateStudentModal from '../../components/CreateStudentModal/CreateStudentModal';
import CommonAlert from '../../common/CommonAlert/CommonAlert';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';

const MasterTeacherView = ({ cookies }) => {
  const VIEW_ALL = 'All Sites';
  const ALL_OPTION = 'All';
  const cycles = ['1', '2', '3', '4', ALL_OPTION];

  const [allData, setAllData] = useState([]); // all student group data
  const [siteStatus, setSiteStatus] = useState('active');
  const [isAdmin, setIsAdmin] = useState(false);
  const isAdminRef = useRef(false);
  const filterVersion = useRef(0);
  const [allSites, setAllSites] = useState({}); // list of sites with associated site id and address
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState();
  const [selectedSiteAddress, setSelectedSiteAddress] = useState();
  const [selectedSchoolYear, setSelectedSchoolYear] = useState(ALL_OPTION);
  const [selectedCycle, setSelectedCycle] = useState(ALL_OPTION);
  const [siteGroups, setSiteGroups] = useState([]); // filtered student groups for site id
  const [studentGroups, setStudentGroups] = useState([]); // filtered student groups additionally on year and cycle
  const [siteStudents, setSiteStudents] = useState([]); // filtered students additionally on year and cycle
  const [schoolYears, setSchoolYears] = useState([]); // all past school years from student groups + current year
  const [categoricalPre, setCategoricalPre] = useState([]); // attitudinal + academic
  const [categoricalPost, setCategoricalPost] = useState([]); // attitudinal + academic
  const [sitePre, setSitePre] = useState([]); // site vs. other TLP
  const [sitePost, setSitePost] = useState([]); // site vs. other TLP
  const [siteToggle, setSiteToggle] = useState(true); // true if want to there are enough options to toggle between
  const [yearToggle, setYearToggle] = useState(true);
  const [createStudentGroupIsOpen, setCreateStudentGroupIsOpen] = useState(false);
  const [createStudentIsOpen, setCreateStudentIsOpen] = useState(false);
  const [showAllStudents, setShowAllStudents] = useState(false);
  const [masterTeacherId, setMasterTeacherId] = useState();
  const [loading, setLoading] = useState(true);
  const [errorOpen, setErrorOpen] = useState(false);

  // used to get the linked site name from student or student group
  const location = useLocation();

  const visibleSites = reportingSites(allSites, siteStatus);
  const activeSiteIds = useMemo(
    () =>
      Object.values(allSites)
        .filter(site => site.active === true)
        .map(site => site.siteId),
    [allSites],
  );
  const hasVisibleSites = Object.keys(visibleSites).length > 0;
  const selectedSiteActive = allSites[selectedSiteName]?.active === true;
  const canCreateStudent =
    selectedSiteName === VIEW_ALL
      ? siteStatus !== 'inactive' && activeSiteIds.length > 0
      : selectedSiteActive;

  const clearDisplay = () => {
    // used for sites with no groups/students
    filterVersion.current += 1;
    setSchoolYears([]);
    setSelectedSchoolYear(ALL_OPTION);
    setSelectedCycle(ALL_OPTION);
    setYearToggle(false);
    setSiteGroups([]);
    setSiteStudents([]);
    setStudentGroups([]);
    setCategoricalPre([]);
    setCategoricalPost([]);
    setSitePre([]);
    setSitePost([]);
  };

  const filterSchoolYearCycle = async (
    filterOptions,
    groups = siteGroups,
    siteName = selectedSiteName,
    siteId = selectedSiteId,
  ) => {
    filterVersion.current += 1;
    const version = filterVersion.current;
    let year = selectedSchoolYear;
    let cycle = selectedCycle;

    // allow for optionally passing in year and cycle to change filter
    // if a year is specified, set the initial cycle to ALL_O
    // if just cycle is specified, update the cycle
    if (filterOptions.year) {
      year = filterOptions.year.substring(0, 4);
      cycle = ALL_OPTION;
    } else {
      cycle = filterOptions?.cycle;
    }
    setSelectedCycle(cycle);

    // filter data based on new year and cycle configurations
    const filteredGroups = groups.filter(
      group =>
        (year !== ALL_OPTION ? group.year === parseInt(year, 10) : true) &&
        (cycle !== ALL_OPTION ? String(group.cycle) === String(cycle) : true),
    );
    setStudentGroups(filteredGroups);

    const students = [];

    filteredGroups.forEach(group => {
      // in case the student group has no students, the students array will be null
      if (group.students && group.students.length > 0) {
        group.students.forEach(student => {
          students.push(student);
        });
      }
    });
    setSiteStudents(students);

    if (students.length === 0) {
      setCategoricalPre([]);
      setCategoricalPost([]);
      setSitePre([]);
      setSitePost([]);
      return;
    }

    const scores = calculateScores(students);
    setCategoricalPre(scores.pre ?? []);
    setCategoricalPost(scores.post ?? []);
    setSitePre([]);
    setSitePost([]);

    // An Admin's All Sites is organization-wide; there is no 'other teacher'
    // cohort to compare it with. Keep the selected report's existing averages.
    if (isAdminRef.current && siteName === VIEW_ALL) return;

    let otherSites = [];
    try {
      if (siteName === VIEW_ALL) {
        const teacherId = cookies.get(cookieKeys.USER_ID);
        otherSites = await TLPBackend.get(`/students/other-teachers/${teacherId}`);
      } else {
        otherSites = await TLPBackend.get(`/students/other-sites/${siteId}`);
      }
    } catch (err) {
      // Keep the selected report available if its comparison read fails.
      // An older request must not replace the new filter's state or error.
      if (version === filterVersion.current) setErrorOpen(true);
      return;
    }
    if (version !== filterVersion.current) return;
    const otherSiteData = otherSites.data.filter(
      student =>
        (year === ALL_OPTION || Number(student.year) === Number(year)) &&
        (cycle === ALL_OPTION || String(student.cycle) === String(cycle)),
    );
    const otherSiteScores = calculateScores(otherSiteData);

    if (scores.pre) {
      setCategoricalPre(scores.pre);
      setCategoricalPost(scores.post);

      const siteScores = calculateSiteScores(scores, otherSiteScores);
      setSitePre(siteScores.pre);
      setSitePost(siteScores.post);
    }
  };

  // handlers for changing school year and cycle
  const filterBySchool = async newYear => {
    setSelectedSchoolYear(newYear);
    await filterSchoolYearCycle({ year: newYear });
  };

  const filterByCycle = async newCycle => {
    await filterSchoolYearCycle({ cycle: newCycle });
  };

  // filter site data using the given siteId, school year, cycle
  // default params for first filtering of Site Data, all other times use the useEffect params
  const filterSiteData = async (
    siteName = selectedSiteName,
    siteId = selectedSiteId,
    data = allData,
    sites = visibleSites,
  ) => {
    let groups = reportingGroups(data, sites);
    if (siteName !== VIEW_ALL) {
      // all groups of selected site
      groups = groups.filter(group => group.siteId === siteId);
    }

    const years = new Set();
    const currDay = new Date();
    let filterOpts = {};

    // if there are any groups in given site, get all relevant year and cycle info
    if (groups.length === 0) {
      clearDisplay();
      return;
    }
    // every school year the selected site/option has groups for
    groups.forEach(group => {
      years.add(formatSchoolYear(group.year));
    });

    // Past June the new school year has to be selectable before any group
    // exists for it. getSchoolYearChoices ends one year past the school year in
    // progress, so its second to last entry is the year we are in -- taking it
    // from the shared list keeps this dropdown on the same roll date as every
    // other school year dropdown.
    if (currDay.getMonth() >= 6) {
      const allSchoolYears = getSchoolYearChoices({ format: 'short', now: currDay });
      years.add(allSchoolYears[allSchoolYears.length - 2]);
    }

    // The menu gets its options either way. The single year case used to skip
    // setSchoolYears, so the dropdown sat empty (or still showing the last
    // site's years) with the toggle disabled and nothing to pick.
    const yearOptions = Array.from(years).sort().reverse();
    if (yearOptions.length === 1) {
      // only one year of data, so display that year instead of "All"
      const [onlyYear] = yearOptions;
      setSelectedSchoolYear(onlyYear);
      filterOpts = { year: onlyYear };
    } else {
      setSelectedSchoolYear(ALL_OPTION);
      filterOpts = { year: ALL_OPTION };
      yearOptions.push(ALL_OPTION);
    }
    setSchoolYears(yearOptions);
    setYearToggle(true);

    setSiteGroups(groups);
    await filterSchoolYearCycle(filterOpts, groups, siteName, siteId);
  };

  const refreshCreatedGroup = async () => {
    const { data } = await TLPBackend.get(`/student-groups/site/${selectedSiteId}`, {
      timeout: 30000,
    });
    setAllData(previous => [...previous.filter(group => group.siteId !== selectedSiteId), ...data]);
    await filterSiteData(selectedSiteName, selectedSiteId, data);
  };

  // update site info for new selected site
  const setSiteInfo = async name => {
    setSelectedSiteName(name);
    setSelectedSiteId(allSites[name]?.siteId);
    setSelectedSiteAddress(allSites[name]?.address);
    setShowAllStudents(false);
    await filterSiteData(name, allSites[name]?.siteId);
  };

  const changeSiteStatus = async event => {
    const status = event.target.value;
    const nextSites = reportingSites(allSites, status);
    const groups = reportingGroups(allData, nextSites);
    const name = initialReportingSite(nextSites, groups, null);
    setSiteStatus(status);
    setSelectedSiteName(name);
    setSelectedSiteId(nextSites[name]?.siteId ?? null);
    setSelectedSiteAddress(nextSites[name]?.address ?? null);
    setSiteToggle(Object.keys(nextSites).length > 1);
    setShowAllStudents(false);
    try {
      await filterSiteData(name, nextSites[name]?.siteId, allData, nextSites);
    } catch (err) {
      setErrorOpen(true);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchTeacherData = async () => {
      try {
        const teacherId = cookies.get(cookieKeys.USER_ID);
        const teacher = await TLPBackend.get(`/teachers/${teacherId}`);
        const admin = teacher.data.position === 'admin';
        const { data } = await TLPBackend.get(
          admin ? '/student-groups' : `/student-groups/master-teacher/${teacherId}`,
        );
        let sites;
        if (admin) {
          // Admin reports include every group's site, independently of who
          // teaches the group. The server's requireAdmin guard authorizes this.
          const response = await TLPBackend.get('/sites');
          sites = response.data;
        } else {
          // Preserve both existing scope lenses: current staffing and sites
          // attached to the teacher's historical groups. Each detail read is
          // still guarded server-side; never ask for the Admin site catalog.
          const ids = new Set([
            ...(teacher.data.sites ?? []).map(site => site.siteId),
            ...data.map(group => group.siteId),
          ]);
          const responses = await Promise.allSettled(
            Array.from(ids).map(async id => (await TLPBackend.get(`/sites/${id}`)).data),
          );
          sites = responses
            .filter(response => response.status === 'fulfilled')
            .map(response => response.value);
          if (!cancelled && responses.some(response => response.status === 'rejected'))
            setErrorOpen(true);
        }
        if (cancelled) return;
        const siteMap = reportingSiteMap(sites);
        const sitesById = new Map(Object.values(siteMap).map(site => [site.siteId, site]));
        const groups = data.map(group => ({
          ...group,
          siteAddress: sitesById.get(group.siteId)?.address,
          students:
            group.students?.map(student => ({
              ...student,
              areaName: group.areaName,
              siteName: group.siteName,
            })) ?? null,
        }));
        // A valid back link into an inactive site keeps the historical view.
        // Ordinary visits start with active sites; this changes no permission.
        const requestedSites = sites.filter(site => site.siteName === location.state?.siteName);
        const requestedName =
          requestedSites.length === 1
            ? Object.keys(siteMap).find(name => siteMap[name].siteId === requestedSites[0].siteId)
            : null;
        const status = siteMap[requestedName]?.active === false ? 'inactive' : 'active';
        const availableSites = reportingSites(siteMap, status);
        const availableGroups = reportingGroups(groups, availableSites);
        const name = initialReportingSite(availableSites, availableGroups, requestedName);
        isAdminRef.current = admin;
        setIsAdmin(admin);
        setMasterTeacherId(Number(teacherId));
        setAllData(groups);
        setAllSites(siteMap);
        setSiteStatus(status);
        setSelectedSiteName(name);
        setSelectedSiteId(availableSites[name]?.siteId ?? null);
        setSelectedSiteAddress(availableSites[name]?.address ?? null);
        setSiteToggle(Object.keys(availableSites).length > 1);
        window.history.replaceState({}, document.title);
        await filterSiteData(name, availableSites[name]?.siteId, groups, availableSites);
      } catch (err) {
        if (!cancelled) setErrorOpen(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTeacherData();
    return () => {
      cancelled = true;
      filterVersion.current += 1;
    };
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div>
      <CommonAlert variant="danger" open={errorOpen} setOpen={setErrorOpen} openTime={3600000}>
        Some report data could not be loaded. Displayed results may be incomplete. Please refresh
        the page to try again.
      </CommonAlert>
      <div className={styles.main}>
        <div className={`${styles.section} ${styles['toggle-container']}`}>
          <div className={styles['report-scope']}>
            <label htmlFor="report-site-status">
              Site status
              <select id="report-site-status" value={siteStatus} onChange={changeSiteStatus}>
                <option value="active">Active sites</option>
                <option value="inactive">Inactive sites</option>
                <option value="all">All site statuses</option>
              </select>
            </label>
            <p>
              {isAdmin
                ? 'Reports cover all sites in the organization.'
                : 'Reports cover only sites already in your assigned or historical scope.'}
            </p>
          </div>
          <div className={styles['toggle-bar']}>
            {/* only show option to select site if there is more than one site */}
            <div>
              <DropdownMenu
                className={styles['dropdown-btn']}
                choices={[...Object.keys(visibleSites), VIEW_ALL]}
                current={selectedSiteName}
                setFn={setSiteInfo}
                disabled={!siteToggle}
              />
            </div>

            <div className={styles['inner-toggle-bar']}>
              <div className={styles['inner-toggle-bar']}>
                <p className={styles['filter-label']}>School Year</p>
                <DropdownMenu
                  className={styles['dropdown-btn']}
                  choices={schoolYears}
                  current={selectedSchoolYear}
                  setFn={filterBySchool}
                  disabled={!yearToggle}
                />
              </div>

              <div className={styles['inner-toggle-bar']}>
                <p className={styles['filter-label']}>Cycle</p>
                <DropdownMenu
                  className={styles['dropdown-btn']}
                  choices={cycles}
                  current={selectedCycle}
                  setFn={filterByCycle}
                />
              </div>
            </div>
          </div>
        </div>

        {selectedSiteName !== VIEW_ALL && (
          <div className={styles.section}>
            <h3>{selectedSiteName}</h3>
            <h3 className={styles['gray-text']}>{selectedSiteAddress}</h3>
          </div>
        )}

        {siteStatus !== 'active' && (
          <p className={styles['history-note']}>
            Inactive sites remain available for historical results. New students and groups can only
            be added at active sites.
          </p>
        )}
        {!hasVisibleSites && (
          <p className={styles['history-note']}>
            No {siteStatus === 'all' ? '' : `${siteStatus} `}sites are available in your current
            scope.
          </p>
        )}
        {studentGroups.length === 0 ? (
          // There are no student groups across any of the assigned sites
          <>
            <div className={styles.section}>
              <h3>Data</h3>
              <div className={styles['empty-view']}>
                <h2>No data is available for this selected time.</h2>
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.header}>
                <h3>Student Groups</h3>
                {hasVisibleSites && (
                  <Button
                    variant="warning"
                    className={styles['create-button']}
                    disabled={typeof selectedSiteId !== 'number' || !selectedSiteActive}
                    onClick={() => setCreateStudentGroupIsOpen(true)}
                  >
                    Create Student Group
                    <img className={styles.plus__icon} src={Plus} alt="Plus Icon" />
                  </Button>
                )}
              </div>
              {selectedSiteName === VIEW_ALL && (
                <p>Choose a site above to create a student group.</p>
              )}
              {studentGroups.length === 0 ? (
                <div className={styles['empty-view']}>
                  <h2>No student group is available for this selected time.</h2>
                </div>
              ) : (
                <div className={styles.content}>
                  {studentGroups
                    .sort((a, b) => (a.groupId > b.groupId ? 1 : -1))
                    .map(group => (
                      <StudentGroup
                        key={group.groupId}
                        groupId={group.groupId}
                        groupName={group.name}
                        studentList={
                          group.students
                            ? group.students.map(s => {
                                return s.firstName;
                              })
                            : []
                        }
                        meetingDay={group.meetingDay}
                        meetingTime={group.meetingTime}
                        viewAddress={selectedSiteName === VIEW_ALL}
                        siteName={group.siteName}
                        address={group.siteAddress}
                      />
                    ))}
                </div>
              )}
            </div>
            {typeof masterTeacherId === 'number' ? (
              <CreateStudentGroupModal
                siteId={selectedSiteId}
                teacherId={masterTeacherId}
                onCreated={refreshCreatedGroup}
                isOpen={createStudentGroupIsOpen}
                setIsOpen={setCreateStudentGroupIsOpen}
              />
            ) : null}
            <div className={`${styles.section} ${styles['students-container']}`}>
              <div className={styles.header}>
                <h3>Students</h3>
                {hasVisibleSites && (
                  <Button
                    variant="warning"
                    className={styles['create-button']}
                    disabled={!canCreateStudent}
                    onClick={() => setCreateStudentIsOpen(true)}
                  >
                    Create New Student
                    <img className={styles.plus__icon} src={Plus} alt="Plus Icon" />
                  </Button>
                )}
                {siteStudents.length > 6 && (
                  <Button
                    className={styles['view-all-button']}
                    aria-expanded={showAllStudents}
                    onClick={() => setShowAllStudents(prev => !prev)}
                  >
                    <p className={styles['view-all-text']}>
                      {showAllStudents ? 'Show Less' : 'View All'}
                    </p>
                  </Button>
                )}
              </div>
              {typeof masterTeacherId === 'number' &&
              (typeof selectedSiteId === 'number' || selectedSiteId == null) ? (
                <CreateStudentModal
                  siteId={selectedSiteId ?? null}
                  teacherId={masterTeacherId}
                  activeSiteIds={activeSiteIds}
                  isOpen={createStudentIsOpen}
                  setIsOpen={setCreateStudentIsOpen}
                />
              ) : null}
              {siteStudents.length === 0 ? (
                <div className={styles['empty-view']}>
                  <h2>No students have been created for this selected time.</h2>
                </div>
              ) : (
                <div className={styles.content}>
                  {selectedSiteName !== VIEW_ALL ? (
                    (showAllStudents ? siteStudents : siteStudents.slice(0, 6)).map(s => (
                      <StudentProfileBox
                        key={s.studentId}
                        studentId={s.studentId}
                        studentName={`${s.firstName} ${s.lastName}`}
                      />
                    ))
                  ) : (
                    <StudentTable
                      data={showAllStudents ? siteStudents : siteStudents.slice(0, 6)}
                    />
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          // There is at least one student group over all assigned sites
          <>
            <div className={styles.section}>
              <h3>Data</h3>
              {categoricalPre.length === 0 ? (
                <div className={styles['empty-view']}>
                  <h2>No data is available for this selected time.</h2>
                </div>
              ) : (
                <div className={styles['graph-container']}>
                  <div className={styles.graph}>
                    <Graph
                      title={`Average Scores for ${selectedSiteName} Site`}
                      xLabels={['Attitudinal', 'Academic']}
                      preData={categoricalPre}
                      postData={categoricalPost}
                    />
                  </div>
                  {!(isAdmin && selectedSiteName === VIEW_ALL) && (
                    <div className={styles.graph}>
                      <Graph
                        title={`${selectedSiteName} Site Average Scores vs. Other TLP Sites`}
                        xLabels={[`${selectedSiteName}`, 'Other TLP']}
                        preData={sitePre}
                        postData={sitePost}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.header}>
                <h3>Student Groups</h3>
                <Button
                  variant="warning"
                  className={styles['create-button']}
                  disabled={typeof selectedSiteId !== 'number' || !selectedSiteActive}
                  onClick={() => setCreateStudentGroupIsOpen(true)}
                >
                  Create Student Group
                  <img className={styles.plus__icon} src={Plus} alt="Plus Icon" />
                </Button>
              </div>
              {selectedSiteName === VIEW_ALL && (
                <p>Choose a site above to create a student group.</p>
              )}
              {studentGroups.length === 0 ? (
                <div className={styles['empty-view']}>
                  <h2>No student group is available for this selected time.</h2>
                </div>
              ) : (
                <div className={styles.content}>
                  {studentGroups
                    .sort((a, b) => (a.groupId > b.groupId ? 1 : -1))
                    .map(group => (
                      <StudentGroup
                        key={group.groupId}
                        groupId={group.groupId}
                        groupName={group.name}
                        studentList={
                          group.students
                            ? group.students.map(s => {
                                return s.firstName;
                              })
                            : []
                        }
                        meetingDay={group.meetingDay}
                        meetingTime={group.meetingTime}
                        viewAddress={selectedSiteName === VIEW_ALL}
                        siteName={group.siteName}
                        address={group.siteAddress}
                      />
                    ))}
                </div>
              )}
            </div>
            {typeof masterTeacherId === 'number' ? (
              <CreateStudentGroupModal
                siteId={selectedSiteId}
                teacherId={masterTeacherId}
                onCreated={refreshCreatedGroup}
                isOpen={createStudentGroupIsOpen}
                setIsOpen={setCreateStudentGroupIsOpen}
              />
            ) : null}
            <div className={`${styles.section} ${styles['students-container']}`}>
              <div className={styles.header}>
                <h3>Students</h3>
                <Button
                  variant="warning"
                  className={styles['create-button']}
                  disabled={!canCreateStudent}
                  onClick={() => setCreateStudentIsOpen(true)}
                >
                  Create New Student
                  <img className={styles.plus__icon} src={Plus} alt="Plus Icon" />
                </Button>
                {siteStudents.length > 6 && (
                  <Button
                    className={styles['view-all-button']}
                    aria-expanded={showAllStudents}
                    onClick={() => setShowAllStudents(prev => !prev)}
                  >
                    <p className={styles['view-all-text']}>
                      {showAllStudents ? 'Show Less' : 'View All'}
                    </p>
                  </Button>
                )}
              </div>
              {typeof masterTeacherId === 'number' &&
              (typeof selectedSiteId === 'number' || selectedSiteId == null) ? (
                <CreateStudentModal
                  siteId={selectedSiteId ?? null}
                  teacherId={masterTeacherId}
                  activeSiteIds={activeSiteIds}
                  isOpen={createStudentIsOpen}
                  setIsOpen={setCreateStudentIsOpen}
                />
              ) : null}
              {siteStudents.length === 0 ? (
                <div className={styles['empty-view']}>
                  <h2>No students have been created for this selected time.</h2>
                </div>
              ) : (
                <div className={styles.content}>
                  {selectedSiteName !== VIEW_ALL ? (
                    (showAllStudents ? siteStudents : siteStudents.slice(0, 6)).map(s => (
                      <StudentProfileBox
                        key={s.studentId}
                        studentId={s.studentId}
                        studentName={`${s.firstName} ${s.lastName}`}
                      />
                    ))
                  ) : (
                    <StudentTable
                      data={showAllStudents ? siteStudents : siteStudents.slice(0, 6)}
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* <Footer /> */}
    </div>
  );
};

MasterTeacherView.propTypes = {
  cookies: instanceOf(Cookies).isRequired,
};

export default withCookies(MasterTeacherView);
